/**
 * Eigenbau: Die Marke, die einen Laufzustand benennt — mit der Farbe, die ihn meint.
 *
 * Warum nicht `Badge`: Tremors Varianten heißen nach Farben (`default` ist
 * blau, `success` grün). Im Haus ist `--color-blue-*` auf den Akzent umgebogen,
 * damit kopierte Bausteine die Hausfarbe treffen — und damit wurde
 * `variant="default"` für „Läuft" **dasselbe Grün** wie `variant="success"`
 * für „Erfolgreich". Am 13.08.2026 im Bild belegt: eine laufende Pipeline sah
 * aus wie eine erfolgreiche.
 *
 * Die Lösung ist nicht, `Badge` zu ändern — kopierte Tremor-Dateien bleiben
 * unangetastet, das ist die Bedingung dafür, dass die Tokenschicht überhaupt
 * funktioniert. Die Lösung ist ein eigener Baustein auf den `--zustand-*`-Token,
 * die genau für diesen Zweck da sind. Dieselbe Farbe wie im `Tracker`, damit
 * Leiste und Liste dasselbe sagen.
 *
 * Die Fläche bleibt zurückhaltend: eine Marke ordnet ein, sie ruft nicht. Der
 * Ton kommt aus einer aufgehellten Fassung des Zustands, die Schrift aus dem
 * Zustand selbst.
 */

import type { ReactNode } from "react";
import { cx } from "@/lib/utils";
import type { TrackerBlock } from "@/components/Tracker";

/** Dasselbe Vokabular wie im Tracker — ein Zustand, eine Farbe, überall. */
type Farbe = TrackerBlock["farbe"];

const TON: Record<Farbe, { flaeche: string; schrift: string }> = {
  gruen: {
    flaeche: "bg-[color-mix(in_oklab,var(--zustand-erfolg)_16%,transparent)]",
    schrift: "text-[var(--zustand-erfolg)]",
  },
  rot: {
    flaeche: "bg-[color-mix(in_oklab,var(--zustand-fehler)_16%,transparent)]",
    schrift: "text-[var(--zustand-fehler)]",
  },
  blau: {
    flaeche: "bg-[color-mix(in_oklab,var(--zustand-laeuft)_16%,transparent)]",
    schrift: "text-[var(--zustand-laeuft)]",
  },
  gelb: {
    flaeche: "bg-[color-mix(in_oklab,var(--zustand-wartet)_20%,transparent)]",
    schrift: "text-[var(--zustand-wartet)]",
  },
  grau: {
    flaeche: "bg-wisch",
    schrift: "text-tinte3",
  },
};

export function Zustandsmarke({
  farbe,
  children,
  className,
}: {
  farbe: Farbe;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cx(
        "inline-flex flex-none items-center whitespace-nowrap px-2 py-1 text-[11.5px] font-medium",
        TON[farbe].flaeche,
        TON[farbe].schrift,
        className,
      )}
    >
      {children}
    </span>
  );
}
